/* The Library (Run): a notepad for ideas, stories, testimonials and objections.
 *
 * No chat and no Brill — she types, taps what kind of thing it is, and it is
 * saved. The tools that write pick from here (the Social Generator today;
 * Newsletter, Sites, Call Scripts and decks as they are built), and every item
 * says where it has been used. Spec: the "Library + sidebar restructure" doc,
 * 2026-09-17, and the canvas mockup made from it.
 *
 * Screen pattern: nothing to press (a chip IS the save; an open item saves as
 * she leaves a field), the whole row opens it, teal for action, amber only for
 * a testimonial still waiting on permission. Images are not here yet — they
 * live in GHL and arrive with the GHL connection.
 *
 * Owns its store: nothing else on the Momentum screen reads the Library while
 * it is open, so the hub does not need to hold it.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Lightbulb, BookOpen, Quote, MessageCircleQuestion, Search, ChevronDown, ChevronRight, Trash2, X } from "lucide-react";
import { ConfirmDialog } from "../ConfirmDialog";
import { B, pTitleAlone, input, quickBtn } from "./tokens";
import { Bread, IconBtn, SavedTick } from "./ui";
import { MediaPickButton } from "./mediaPicker";
import {
  useLibrary, LIBRARY_KINDS, kindOf, libraryView, libraryCounts, metaFor,
  usedLabel, usedList, isUnused, needsPermission, STORY_MINE, STORY_CLIENT,
} from "../../utils/library";

const KIND_ICON = { idea: Lightbulb, story: BookOpen, testimonial: Quote, objection: MessageCircleQuestion };

const label = { fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: B.label, fontWeight: 700 };
const pill = {
  flexShrink: 0, fontSize: 11, fontWeight: 600, borderRadius: 10, padding: "3px 9px",
  background: B.surface2, color: B.mid, border: `1px solid ${B.border}`, whiteSpace: "nowrap",
};
const warnPill = { ...pill, background: B.amberQuiet, color: B.amber, borderColor: B.amberLine };

function KindBadge({ kind }) {
  const Icon = KIND_ICON[kind] || Lightbulb;
  return (
    <span aria-hidden="true" style={{
      width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
      background: B.surface2, border: `1px solid ${B.border}`, color: B.mid,
    }}>
      <Icon size={16} />
    </span>
  );
}

function Segmented({ options, value, onChange, ariaLabel }) {
  return (
    <div role="group" aria-label={ariaLabel} style={{ display: "inline-flex", border: `1px solid ${B.border2}`, borderRadius: 9, overflow: "hidden", alignSelf: "flex-start" }}>
      {options.map((o) => {
        const on = value === o.id;
        return (
          <button key={o.id} type="button" onClick={() => onChange(o.id)} aria-pressed={on}
            style={{
              minHeight: 34, padding: "0 12px", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5,
              background: on ? B.tealQuiet : "transparent", color: on ? B.teal : B.mid, fontWeight: on ? 600 : 400,
            }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* One labelled field that saves when she leaves it. Holds its own text while
   she types, so a slow save never fights the cursor. */
function BlurField({ id, labelText, value, placeholder, onCommit, multiline, rows = 3, list }) {
  const [text, setText] = useState(value || "");
  const commit = () => { if ((text || "") !== (value || "")) onCommit(text); };
  const common = {
    id, value: text, placeholder, onChange: (e) => setText(e.target.value), onBlur: commit,
    style: { ...input, ...(multiline ? { resize: "vertical", lineHeight: 1.55 } : {}) },
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <label htmlFor={id} style={label}>{labelText}</label>
      {multiline ? <textarea rows={rows} {...common} /> : <input type="text" list={list} {...common} />}
    </div>
  );
}

/* A PICTURE ON AN ITEM — a story's (her ask, 2026-09-28: "stories need the
   ability to upload an image") and a testimonial's headshot. One field for
   both, in `photo` (0023; no new column). From her GHL library where it is
   connected — folders, and Upload into the folder she is in — and a pasted
   link always, because not everyone has GHL (her rule, 2026-09-23).
   `remember` reopens the picker in the folder she last used for this kind. */
function PhotoField({ item, labelText, placeholder, remember, onPatch }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [item.photo]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <span style={label}>{labelText}</span>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        {item.photo && (broken
          ? <span title="Nothing loaded from that link" style={{ width: 44, height: 44, borderRadius: 8, background: B.amberQuiet, border: `1px solid ${B.amberLine}`, flexShrink: 0 }} />
          : <img src={item.photo} alt="" onError={() => setBroken(true)} style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />)}
        <MediaPickButton remember={remember} onPick={(url) => onPatch({ photo: url })} />
        <div style={{ flex: "1 1 200px", minWidth: 0 }}>
          {/* Keyed by the stored link, so a pick from GHL shows here at once —
              the field holds its own text while she types. */}
          <input key={item.photo || ""} type="url" defaultValue={item.photo || ""} aria-label={`${labelText} link`}
            placeholder={placeholder}
            onBlur={(e) => { const v = e.target.value.trim() || null; if (v !== (item.photo || null)) onPatch({ photo: v }); }}
            style={input} />
        </div>
        {item.photo && <IconBtn icon={X} label={`Remove the ${labelText.toLowerCase()}`} onClick={() => onPatch({ photo: null })} size={14} />}
      </div>
      {broken && (
        <div style={{ fontSize: 11.5, color: B.amber, lineHeight: 1.5 }}>
          Nothing loaded from that link. It may need to be public, or it may be the page the picture sits on rather than the picture itself.
        </div>
      )}
    </div>
  );
}

function OpenItem({ item, onPatch, onAskDelete, saving, problem, tagSuggestions, isMobile }) {
  const k = kindOf(item.kind);
  const uses = usedList(item);
  const two = { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 14 };
  const listId = `lib-tags-${item.kind}`;
  const richKind = item.kind === "story" || item.kind === "idea";
  return (
    <div style={{ padding: isMobile ? "4px 14px 14px" : "4px 16px 16px 62px", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* IDEAS AND STORIES OVERLAP A LOT (her call, 2026-09-29): "all the same
          functionality but they can stay in separate buckets". Both get a
          Title, the full-size box and a Picture; each keeps its own filing
          field — an idea by Pillar, a story by whose and Themes. */}
      {richKind && (
        <BlurField id={`title-${item.id}`} labelText="Title" value={item.title} placeholder={`A short name for this ${item.kind}`}
          onCommit={(v) => onPatch({ title: v || null })} />
      )}
      <BlurField id={`text-${item.id}`} labelText={k.textLabel} value={item.text} multiline rows={richKind ? 6 : 3}
        onCommit={(v) => onPatch({ text: v })} />

      {item.kind === "idea" && (
        <div style={{ maxWidth: 360 }}>
          <BlurField id={`tag-${item.id}`} labelText="Pillar" value={item.tag} placeholder="Unfiled" list={listId}
            onCommit={(v) => onPatch({ tag: v.trim() || null })} />
        </div>
      )}

      {item.kind === "story" && (
        <div style={two}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={label}>Whose story</span>
            <Segmented ariaLabel="Whose story" value={item.whose || STORY_MINE}
              options={[{ id: STORY_MINE, label: "Mine" }, { id: STORY_CLIENT, label: "A client's" }]}
              onChange={(v) => onPatch({ whose: v })} />
          </div>
          <BlurField id={`tag-${item.id}`} labelText="Themes" value={item.tag} placeholder="Resilience, pricing…" list={listId}
            onCommit={(v) => onPatch({ tag: v.trim() || null })} />
        </div>
      )}
      {richKind && (
        <PhotoField item={item} labelText="Picture" remember={item.kind} onPatch={onPatch}
          placeholder="Optional — or paste a link to a picture" />
      )}

      {item.kind === "testimonial" && (
        <>
          <div style={two}>
            <BlurField id={`who-${item.id}`} labelText="Who said it" value={item.who} placeholder="Name, what they do"
              onCommit={(v) => onPatch({ who: v.trim() || null })} />
            <BlurField id={`result-${item.id}`} labelText="Result they got" value={item.result} placeholder="What changed for them"
              onCommit={(v) => onPatch({ result: v.trim() || null })} />
          </div>
          {/* Their headshot (0023). From GHL now, or a pasted link. It travels
              with the quote into a page or a slide. */}
          <PhotoField item={item} labelText="Photo" remember="testimonial" onPatch={onPatch}
            placeholder="Optional — or paste a link to their headshot" />
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13.5, color: B.text, cursor: "pointer", alignSelf: "flex-start" }}>
            <input type="checkbox" checked={!!item.permission} onChange={(e) => onPatch({ permission: e.target.checked })}
              style={{ width: 18, height: 18, accentColor: "var(--accent)" }} />
            I have permission to use this publicly
          </label>
        </>
      )}

      {item.kind === "objection" && (
        <>
          <BlurField id={`answer-${item.id}`} labelText="Your answer" value={item.answer} multiline
            placeholder="How you respond when you hear this" onCommit={(v) => onPatch({ answer: v || null })} />
          <div style={{ maxWidth: 360 }}>
            <BlurField id={`tag-${item.id}`} labelText="Tags" value={item.tag} placeholder="Discovery call, pricing…" list={listId}
              onCommit={(v) => onPatch({ tag: v.trim() || null })} />
          </div>
        </>
      )}

      <datalist id={listId}>{tagSuggestions.map((t) => <option key={t} value={t} />)}</datalist>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={label}>Used in</span>
        {uses.length ? (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13, color: B.text }}>
            {uses.map((u, i) => <span key={`${u.id || "p"}-${i}`}>{u.label || "Untitled"}</span>)}
          </div>
        ) : (
          <span style={{ fontSize: 13, color: B.mid }}>Not used yet.</span>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", borderTop: `1px solid ${B.border}`, paddingTop: 12 }}>
        <SavedTick saving={saving} problem={problem} />
        <span style={{ flex: 1 }} />
        <label htmlFor={`kind-${item.id}`} style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Kind</label>
        <select id={`kind-${item.id}`} value={item.kind} onChange={(e) => onPatch({ kind: e.target.value })}
          style={{ ...input, width: 150, padding: "7px 10px" }}>
          {LIBRARY_KINDS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
        </select>
        <button type="button" onClick={onAskDelete}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 34, padding: "0 12px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, background: "transparent", color: B.salmon, border: `1px solid ${B.salmonLine}` }}>
          <Trash2 size={13} /> Delete
        </button>
      </div>
    </div>
  );
}

/* `crumb` is null when it opens over a chat (LibraryOverlay): "Run" names a
   Momentum group an Ideation or Accelerator member has never seen. */
/* `start` — { kind, id } — opens the Library on what was just saved elsewhere
   (Save that thought in Ask Brill): filtered to its kind, the item lit the
   way a save here lights it. */
export function Library({ isMobile = false, crumb = "Run", start = null }) {
  const { rows, loading, error, save, remove, clearError } = useLibrary({ refresh: true });
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(null);
  const [filter, setFilter] = useState(start?.kind || "all");
  const [sort, setSort] = useState("newest");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [flashId, setFlashId] = useState(start?.id || null);
  useEffect(() => {
    if (!start?.id) return undefined;
    const t = setTimeout(() => setFlashId((id) => (id === start.id ? null : id)), 2400);
    return () => clearTimeout(t);
  }, [start?.id]);
  const [savingId, setSavingId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [pic, setPic] = useState(null);

  const counts = useMemo(() => libraryCounts(rows), [rows]);
  const list = useMemo(() => libraryView(rows, { kind: filter, sort, q }), [rows, filter, sort, q]);
  const tagSuggestions = useMemo(() => {
    const out = {};
    rows.forEach((r) => { if (r.tag) (out[r.kind] = out[r.kind] || new Set()).add(r.tag); });
    return out;
  }, [rows]);

  const addAs = async (kind) => {
    const text = draft.trim();
    if ((!text && !pic) || adding) return;
    setAdding(kind);
    const saved = await save({
      kind, text, usedBy: [], permission: false,
      whose: kind === "story" ? STORY_MINE : null,
      ...(pic ? { photo: pic } : {}),
    });
    setAdding(null);
    if (!saved) return;              // the banner says why; her words stay in the box
    setDraft("");
    setPic(null);
    setQ("");
    if (filter !== "all" && filter !== kind) setFilter("all");
    setFlashId(saved.id);
    setTimeout(() => setFlashId((id) => (id === saved.id ? null : id)), 1800);
  };

  /* SAVE AGAINST THE NEWEST ROW, NEVER THE ONE THIS ROW RENDERED WITH.
     Every save sends the whole item, so two quick edits — type a photo link,
     tick permission before the first save comes back — made the second write
     the item as it was BEFORE the first, and the photo silently went back to
     empty. Caught clicking through on 2026-09-17: the link was on screen and
     the database had null. `rowsRef` is the store's current rows, so a patch
     always merges into what was last saved. */
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const patch = async (item, change) => {
    setSavingId(item.id);
    const base = rowsRef.current.find((r) => r.id === item.id) || item;
    await save({ ...base, ...change });
    setSavingId((id) => (id === item.id ? null : id));
  };

  const askDelete = (item) => setConfirm({
    title: `Delete this ${kindOf(item.kind).label.toLowerCase()}?`,
    message: `"${item.text.length > 80 ? item.text.slice(0, 80) + "…" : item.text}" will be removed from your Library.`
      + (usedList(item).length ? " Anything already written from it keeps its words." : "")
      + " This can't be undone.",
    onConfirm: async () => {
      setConfirm(null);
      const ok = await remove(item.id);
      if (ok && openId === item.id) setOpenId(null);
    },
  });

  const filters = [{ id: "all", label: "All" }, ...LIBRARY_KINDS.map((k) => ({ id: k.id, label: k.plural }))];
  /* A picture alone is worth keeping too — the photo that IS the story. */
  const noDraft = !draft.trim() && !pic;
  /* A PICTURE FROM THE QUICK BOX (her ask, 2026-09-28: "can you have an image
     picker up here also?"). Picked before she chooses what it is, it rides on
     whichever kind she saves as, into `photo`. */
  const picker = <MediaPickButton remember="library" label="Picture" onPick={(url) => setPic(url)} />;
  const attached = pic && (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <img src={pic} alt="" style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8 }} />
      <span style={{ fontSize: 12, color: B.dim }}>Saved with whatever you save this as</span>
      <IconBtn icon={X} label="Take the picture off" onClick={() => setPic(null)} size={14} />
    </div>
  );

  return (
    <div style={{ maxWidth: 960 }}>
      {crumb && <Bread text={crumb} />}
      <h2 style={pTitleAlone}>Library</h2>

      {error && (
        <div role="alert" onClick={clearError} style={{ background: B.salmonQuiet, border: `1px solid ${B.salmonLine}`, borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: B.salmon, cursor: "pointer" }}>
          {error}
        </div>
      )}

      <section aria-label="Jot something down" style={{ background: B.surface, border: `1px solid ${B.border2}`, borderRadius: 14, padding: isMobile ? 14 : "18px 20px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
        <label htmlFor="lib-quick" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Jot something down</label>
        <textarea id="lib-quick" rows={3} value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder="What's on your mind?"
          style={{ resize: "none", border: "none", background: "transparent", color: B.text, fontSize: 15, lineHeight: 1.55, outline: "none", padding: 0, fontFamily: "inherit" }} />
        {attached}
        {isMobile && <div>{picker}</div>}
        {isMobile ? (
          /* ON A PHONE, ONE ROW OF ICONS (her ask, 2026-09-25: "a little nicer in
             mobile … a huge selling point"). Four pills wrapped to two lines and
             read as a form; four icons in one row read as one thumb-tap. A small
             word stays under each: a lightbulb and a book are not obvious to
             someone on her first day, and the word costs no width. */
          <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 12 }}>
            <div style={{ ...label, marginBottom: 8 }}>Save as</div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${LIBRARY_KINDS.length}, 1fr)`, gap: 8 }}>
              {LIBRARY_KINDS.map((k) => {
                const Icon = KIND_ICON[k.id];
                return (
                  <button key={k.id} type="button" onClick={() => addAs(k.id)} disabled={noDraft || !!adding}
                    aria-label={`Save as ${k.label}`} title={`Save as ${k.label}`}
                    style={{
                      ...quickBtn, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                      minHeight: 58, padding: "8px 4px", borderRadius: 14,
                      opacity: noDraft ? 0.4 : 1, cursor: noDraft || adding ? "default" : "pointer",
                    }}>
                    <Icon size={20} />
                    <span style={{ fontSize: 10.5, fontWeight: 600, lineHeight: 1.1 }}>{adding === k.id ? "Saving…" : k.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", borderTop: `1px solid ${B.border}`, paddingTop: 14 }}>
          <span style={{ ...label, marginRight: 4 }}>Save as</span>
          {LIBRARY_KINDS.map((k) => {
            const Icon = KIND_ICON[k.id];
            return (
              <button key={k.id} type="button" onClick={() => addAs(k.id)} disabled={noDraft || !!adding}
                style={{
                  ...quickBtn, display: "inline-flex", alignItems: "center", gap: 7, minHeight: isMobile ? 44 : 36,
                  padding: "0 14px", borderRadius: 18, fontSize: 13, fontWeight: 600,
                  opacity: noDraft ? 0.4 : 1, cursor: noDraft || adding ? "default" : "pointer",
                }}>
                <Icon size={15} />
                {adding === k.id ? "Saving…" : k.label}
              </button>
            );
          })}
          <span style={{ marginLeft: "auto" }}>{picker}</span>
        </div>
        )}
      </section>

      {loading && rows.length === 0 ? (
        <p style={{ margin: "20px 0 0", fontSize: 13.5, color: B.mid }}>Loading your Library…</p>
      ) : rows.length === 0 ? (
        <p style={{ margin: "16px 0 0", fontSize: 13.5, lineHeight: 1.6, color: B.mid, maxWidth: 560 }}>
          Anything you jot here can be pulled into your posts, pages, scripts and decks.
        </p>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", margin: "20px 0 12px" }}>
            <div role="tablist" aria-label="Filter by kind"
              style={{ display: "flex", gap: 4, ...(isMobile ? { overflowX: "auto", flexWrap: "nowrap", width: "100%" } : { flexWrap: "wrap" }) }}>
              {filters.map((f) => {
                const on = filter === f.id;
                return (
                  <button key={f.id} role="tab" type="button" aria-selected={on} onClick={() => { setFilter(f.id); setOpenId(null); }}
                    style={{
                      flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, minHeight: 36, padding: "0 12px",
                      borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 13, whiteSpace: "nowrap",
                      border: `1px solid ${on ? B.tealLine : "transparent"}`, background: on ? B.tealQuiet : "transparent",
                      color: on ? B.teal : B.mid, fontWeight: on ? 600 : 500,
                    }}>
                    {f.label}<span style={{ fontSize: 11, color: on ? B.teal : B.dim }}>{counts[f.id]}</span>
                  </button>
                );
              })}
            </div>
            <span style={{ flex: 1 }} />
            <Segmented ariaLabel="Sort" value={sort} onChange={setSort}
              options={[{ id: "newest", label: "Newest" }, { id: "unused", label: "Unused first" }]} />
            <div style={{ position: "relative", width: isMobile ? "100%" : 220 }}>
              <label htmlFor="lib-search" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Search the Library</label>
              <input id="lib-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search"
                style={{ ...input, padding: "8px 12px 8px 34px" }} />
              <Search size={15} aria-hidden="true" style={{ position: "absolute", left: 12, top: 11, color: B.dim }} />
            </div>
          </div>

          {/* WHY STORIES MATTER, said where she saves them (her rule, 2026-09-25):
              Brill never invents a client story, and draws on these in her
              posts — so the more real ones here, the more it can use. */}
          {filter === "story" && (
            <p style={{ margin: "0 0 10px", fontSize: 12.5, lineHeight: 1.6, color: B.mid }}>
              Brill draws on these in your posts, and never makes a story up. Save yours and your clients' (mark whose in each one) — the more real stories here, the more it can use.
            </p>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {list.map((item) => {
              const open = openId === item.id;
              const uses = usedList(item);
              return (
                <div key={item.id} style={{
                  border: `1px solid ${flashId === item.id ? B.tealLine : open ? B.border2 : B.border}`,
                  borderRadius: 12, background: flashId === item.id ? B.tealQuiet : B.surface, transition: "background 0.4s, border-color 0.2s",
                }}>
                  <button type="button" aria-expanded={open} onClick={() => setOpenId(open ? null : item.id)}
                    style={{
                      width: "100%", display: "flex", alignItems: isMobile ? "flex-start" : "center", gap: isMobile ? 10 : 14,
                      flexDirection: isMobile ? "column" : "row", padding: isMobile ? 14 : "12px 16px", minHeight: 56,
                      border: "none", background: "transparent", color: B.text, textAlign: "left", cursor: "pointer",
                      fontFamily: "inherit", borderRadius: 12, boxSizing: "border-box",
                    }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 10, width: isMobile ? "100%" : "auto", flexShrink: 0 }}>
                      <KindBadge kind={item.kind} />
                      {isMobile && <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: B.mid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{metaFor(item)}</span>}
                      {isMobile && needsPermission(item) && <span style={warnPill}>No permission</span>}
                      {isMobile && <span style={{ ...pill, color: uses.length ? B.text : B.mid }}>{usedLabel(item)}</span>}
                    </span>
                    {/* A CLOSED ROW IS A PREVIEW (her ask, 2026-10-15: "collapse the
                        library stories in mobile and desktop, those are going to
                        get long"). One line on a desktop, two on a phone, then
                        "…" — the whole text is one press away, in the open item.
                        It used to show every word on a phone. */}
                    <span data-preview style={{
                      flex: 1, minWidth: 0, fontSize: 14, lineHeight: 1.45, overflow: "hidden",
                      ...(isMobile
                        ? { display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, width: "100%" }
                        : { textOverflow: "ellipsis", whiteSpace: "nowrap" }),
                    }}>
                      {(item.kind === "story" || item.kind === "idea") && item.title ? <b style={{ fontWeight: 600 }}>{item.title} · </b> : null}
                      {item.text || <span style={{ color: B.dim }}>Empty</span>}
                    </span>
                    {!isMobile && (
                      <>
                        <span style={{ flexShrink: 0, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12.5, color: B.mid }}>{metaFor(item)}</span>
                        {needsPermission(item) && <span style={warnPill}>No permission yet</span>}
                        <span title={uses.length ? uses.map((u) => u.label || "Untitled").join(" · ") : "Not used yet"}
                          style={{ ...pill, color: isUnused(item) ? B.mid : B.text }}>{usedLabel(item)}</span>
                        {open ? <ChevronDown size={14} color={B.dim} /> : <ChevronRight size={14} color={B.dim} />}
                      </>
                    )}
                  </button>
                  {open && (
                    <OpenItem key={`${item.id}:${item.kind}`} item={item} isMobile={isMobile}
                      saving={savingId === item.id}
                      problem={error && savingId === null && openId === item.id ? "Not saved — see the message above" : null}
                      tagSuggestions={[...(tagSuggestions[item.kind] || [])]}
                      onPatch={(change) => patch(item, change)}
                      onAskDelete={() => askDelete(item)} />
                  )}
                </div>
              );
            })}
            {list.length === 0 && (
              <p style={{ margin: "8px 0", fontSize: 13.5, color: B.mid }}>Nothing matches that.</p>
            )}
          </div>
        </>
      )}

      {confirm && (
        <ConfirmDialog title={confirm.title} message={confirm.message} confirmLabel="Delete" danger
          onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />
      )}
    </div>
  );
}
